import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Plus, 
  Loader2, 
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  LayoutList,
  Columns,
  Mail,
  Briefcase,
  User,
  Clock,
  MoreVertical,
  Calendar
} from 'lucide-react';
import { 
  usePipelineStages, 
  useApplications, 
  useUpdateApplicationStage,
  useCreateApplication 
} from '../../hooks/useHiring';
import { useUsers } from '../../hooks/useUsers';
import PipelineColumn from '../../components/hiring/PipelineColumn';
import QuickApplicationModal from '../../components/hiring/QuickApplicationModal';

/**
 * HiringPipeline - Main Kanban board for managing employee applications
 */
const HiringPipeline = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  // State for filters and search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [recruiterFilter, setRecruiterFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal state
  const [quickAddModal, setQuickAddModal] = useState({
    isOpen: false,
    stage: null
  });
  
  // Selected application for detail view (placeholder for future implementation)
  // eslint-disable-next-line no-unused-vars
  const [_selectedApplication, _setSelectedApplication] = useState(null);
  
  // View mode state (kanban/list toggle)
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'
  
  // Auto-detect mobile and switch to list view
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      if (isMobile && viewMode === 'kanban') {
        setViewMode('list');
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [viewMode]);

  // Fetch data
  const { data: stagesData, isLoading: stagesLoading } = usePipelineStages();
  const { data: applicationsData, isLoading: applicationsLoading, refetch } = useApplications({
    status: statusFilter || undefined,
    assigned_to: recruiterFilter || undefined,
    search: searchQuery || undefined
  });
  const { data: usersData } = useUsers();
  
  // Mutations
  const updateStageMutation = useUpdateApplicationStage();
  const createApplicationMutation = useCreateApplication();

  // Extract data - wrapped in useMemo to avoid recreating references on every render
  const stages = useMemo(() => stagesData?.stages || [], [stagesData?.stages]);
  const allApplications = useMemo(() => applicationsData?.applications || [], [applicationsData?.applications]);
  const recruiters = useMemo(() => usersData?.users?.filter(u => u.role === 'admin' || u.role === 'super_admin') || [], [usersData?.users]);

  // Get unique positions for filter dropdown
  const uniquePositions = useMemo(() => {
    const positions = new Set();
    allApplications.forEach(app => {
      if (app.desired_position) {
        positions.add(app.desired_position);
      }
    });
    return Array.from(positions).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allApplications]);

  // Group applications by stage
  const applicationsByStage = useMemo(() => {
    const grouped = {};
    
    // Initialize all stages with empty arrays
    stages.forEach(stage => {
      grouped[stage.name] = [];
    });
    
    // Group applications
    allApplications.forEach(app => {
      const stageName = app.current_stage;
      if (grouped[stageName]) {
        // Apply position filter client-side
        if (!positionFilter || app.desired_position === positionFilter) {
          grouped[stageName].push(app);
        }
      }
    });
    
    return grouped;
  }, [allApplications, stages, positionFilter]);

  // Stats
  const stats = useMemo(() => {
    const total = allApplications.length;
    const active = allApplications.filter(a => a.status === 'active').length;
    const hired = allApplications.filter(a => a.status === 'hired').length;
    const rejected = allApplications.filter(a => a.status === 'rejected').length;
    return { total, active, hired, rejected };
  }, [allApplications]);

  // Handlers
  const handleMoveStage = async (applicationId, newStage, reason = '') => {
    try {
      await updateStageMutation.mutateAsync({ 
        id: applicationId, 
        stage: newStage, 
        reason 
      });
    } catch (error) {
      console.error('Failed to move application:', error);
      alert('Failed to move application: ' + error.message);
    }
  };

  const handleQuickAdd = (stage) => {
    setQuickAddModal({
      isOpen: true,
      stage
    });
  };

  const handleQuickAddSubmit = async (formData) => {
    try {
      await createApplicationMutation.mutateAsync(formData);
      setQuickAddModal({ isOpen: false, stage: null });
    } catch (error) {
      console.error('Failed to create application:', error);
      alert('Failed to create application: ' + error.message);
    }
  };

  const handleScheduleInterview = (application) => {
    // Placeholder - would open interview scheduling modal
    console.log('Schedule interview for:', application);
    alert('Interview scheduling feature coming soon!');
  };

  const handleSendEmail = (application) => {
    // Placeholder - would open email composer
    const subject = `Re: ${application.desired_position || 'Application'} at your organization`;
    const mailtoLink = `mailto:${application.email}?subject=${encodeURIComponent(subject)}`;
    window.open(mailtoLink, '_blank');
  };

  const handleViewDetails = (application) => {
    navigate(`/${tenantId}/dashboard/hiring/applications/${application.id}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setRecruiterFilter('');
    setPositionFilter('');
  };

  const hasActiveFilters = searchQuery || statusFilter || recruiterFilter || positionFilter;

  // Loading state
  if (stagesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in h-full flex flex-col">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Hiring Pipeline</h1>
            <p className="text-gray-500 mt-1">Manage employee applications and hiring workflow.</p>
          </div>
          
          {/* Stats Cards */}
          <div className="flex space-x-3">
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-purple-100 flex items-center space-x-2">
              <Users size={18} className="text-purple-600" />
              <div>
                <p className="text-xs text-gray-500">Total</p>
                <p className="font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-green-100 flex items-center space-x-2">
              <UserCheck size={18} className="text-green-600" />
              <div>
                <p className="text-xs text-gray-500">Hired</p>
                <p className="font-semibold text-green-700">{stats.hired}</p>
              </div>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-red-100 flex items-center space-x-2">
              <UserX size={18} className="text-red-600" />
              <div>
                <p className="text-xs text-gray-500">Rejected</p>
                <p className="font-semibold text-red-700">{stats.rejected}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or position..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input pl-12 w-full"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg border transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-purple-50 border-purple-200 text-purple-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter size={18} />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="bg-purple-600 text-white text-xs rounded-full px-2 py-0.5">
                {[searchQuery, statusFilter, recruiterFilter, positionFilter].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            className="flex items-center space-x-2 px-4 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={applicationsLoading}
          >
            <RefreshCw size={18} className={applicationsLoading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          
          {/* View Mode Toggle */}
          <button
            onClick={() => setViewMode(viewMode === 'kanban' ? 'list' : 'kanban')}
            className="flex items-center space-x-2 px-4 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors lg:hidden"
            title={viewMode === 'kanban' ? 'Switch to list view' : 'Switch to kanban view'}
          >
            {viewMode === 'kanban' ? <LayoutList size={18} /> : <Columns size={18} />}
            <span className="hidden sm:inline">{viewMode === 'kanban' ? 'List' : 'Kanban'}</span>
          </button>

          {/* Quick Add Button */}
          <button
            onClick={() => handleQuickAdd(stages[0])}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Add Application</span>
          </button>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
            {/* Status Filter */}
            <div>
              <label className="form-label">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Recruiter Filter */}
            <div>
              <label className="form-label">Assigned Recruiter</label>
              <select
                value={recruiterFilter}
                onChange={(e) => setRecruiterFilter(e.target.value)}
                className="form-input"
              >
                <option value="">All Recruiters</option>
                {recruiters.map(recruiter => (
                  <option key={recruiter.id} value={recruiter.id}>
                    {recruiter.first_name} {recruiter.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Position Filter */}
            <div>
              <label className="form-label">Position</label>
              <select
                value={positionFilter}
                onChange={(e) => setPositionFilter(e.target.value)}
                className="form-input"
              >
                <option value="">All Positions</option>
                {uniquePositions.map(position => (
                  <option key={position} value={position}>{position}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Active Filter Tags */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-500">Active filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                Search: {searchQuery}
                <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-purple-900">×</button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                Status: {statusFilter}
                <button onClick={() => setStatusFilter('')} className="ml-1 hover:text-purple-900">×</button>
              </span>
            )}
            {recruiterFilter && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                Recruiter: {recruiters.find(r => r.id === recruiterFilter)?.first_name}
                <button onClick={() => setRecruiterFilter('')} className="ml-1 hover:text-purple-900">×</button>
              </span>
            )}
            {positionFilter && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                Position: {positionFilter}
                <button onClick={() => setPositionFilter('')} className="ml-1 hover:text-purple-900">×</button>
              </span>
            )}
            <button
              onClick={clearFilters}
              className="text-xs text-red-600 hover:text-red-700 font-medium"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Kanban Board or List View */}
      {viewMode === 'kanban' ? (
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex space-x-4 pb-4 min-w-max">
            {stages.map(stage => (
              <PipelineColumn
                key={stage.name}
                stage={stage}
                applications={applicationsByStage[stage.name] || []}
                allStages={stages}
                onMoveStage={handleMoveStage}
                onScheduleInterview={handleScheduleInterview}
                onSendEmail={handleSendEmail}
                onViewDetails={handleViewDetails}
                onQuickAdd={handleQuickAdd}
                isLoading={applicationsLoading && !applicationsByStage[stage.name]?.length}
              />
            ))}
          </div>
        </div>
      ) : (
        /* Mobile List View */
        <div className="flex-1 overflow-y-auto pb-4">
          <div className="space-y-4">
            {stages.map(stage => {
              const stageApplications = applicationsByStage[stage.name] || [];
              if (stageApplications.length === 0) return null;
              
              return (
                <div key={stage.name} className="bg-white rounded-lg shadow-sm border">
                  <div 
                    className="px-4 py-3 border-b flex items-center justify-between"
                    style={{ borderLeftColor: stage.color, borderLeftWidth: '4px' }}
                  >
                    <div>
                      <h3 className="font-medium text-gray-900">{stage.label}</h3>
                      <span className="text-sm text-gray-500">{stageApplications.length} applicants</span>
                    </div>
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    />
                  </div>
                  <div className="divide-y divide-gray-100">
                    {stageApplications.map(application => (
                      <ApplicationListItem 
                        key={application.id} 
                        application={application} 
                        stage={stage}
                        onViewDetails={() => handleViewDetails(application)}
                        onScheduleInterview={() => handleScheduleInterview(application)}
                        onSendEmail={() => handleSendEmail(application)}
                        onMoveStage={handleMoveStage}
                        allStages={stages}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Add Modal */}
      <QuickApplicationModal
        isOpen={quickAddModal.isOpen}
        onClose={() => setQuickAddModal({ isOpen: false, stage: null })}
        onSubmit={handleQuickAddSubmit}
        initialStage={quickAddModal.stage}
        recruiters={recruiters}
        isLoading={createApplicationMutation.isPending}
      />
    </div>
  );
};

/**
 * ApplicationListItem - Compact list item for mobile view
 */
const ApplicationListItem = ({ 
  application, 
  stage,
  onViewDetails,
  onScheduleInterview,
  onSendEmail,
  onMoveStage,
  allStages
}) => {
  const [showActions, setShowActions] = useState(false);

  // Calculate days since created
  const getDaysSince = (dateString) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysSince = getDaysSince(application.created_at);

  // Get initials for avatar
  const getInitials = () => {
    const first = application.first_name?.[0] || '';
    const last = application.last_name?.[0] || '';
    return (first + last).toUpperCase();
  };

  const assignedRecruiter = application.assigned_to_user;
  const currentStageIndex = allStages.findIndex(s => s.name === application.current_stage);
  const canMoveLeft = currentStageIndex > 0;
  const canMoveRight = currentStageIndex < allStages.length - 1 && 
                       application.current_stage !== 'hired' && 
                       application.current_stage !== 'rejected';

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div 
          className="flex items-center space-x-3 flex-1 cursor-pointer"
          onClick={onViewDetails}
        >
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-purple-700 font-semibold text-sm">
              {getInitials()}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-gray-900 text-sm truncate">
              {application.first_name} {application.last_name}
            </h4>
            <div className="flex items-center text-xs text-gray-500 mt-0.5">
              <Mail size={10} className="mr-1" />
              <span className="truncate">{application.email}</span>
            </div>
            <div className="flex items-center text-xs text-gray-600 mt-1">
              <Briefcase size={10} className="mr-1 text-purple-500" />
              <span className="font-medium truncate">{application.desired_position || 'No position specified'}</span>
            </div>
            <div className="flex items-center text-xs text-gray-500 mt-1 space-x-3">
              <span className="flex items-center">
                <Clock size={10} className="mr-1" />
                {daysSince === 0 ? 'Today' : daysSince === 1 ? '1 day ago' : `${daysSince} days ago`}
              </span>
              {assignedRecruiter ? (
                <span className="flex items-center">
                  <User size={10} className="mr-1" />
                  {assignedRecruiter.first_name} {assignedRecruiter.last_name?.[0]}.
                </span>
              ) : (
                <span className="text-gray-400 italic">Unassigned</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="relative ml-2">
          <button 
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
          >
            <MoreVertical size={16} />
          </button>
          
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10"
                onClick={() => setShowActions(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                <button
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails();
                    setShowActions(false);
                  }}
                >
                  <User size={14} className="mr-2" />
                  View Details
                </button>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onScheduleInterview();
                    setShowActions(false);
                  }}
                >
                  <Calendar size={14} className="mr-2" />
                  Schedule Interview
                </button>
                <button
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSendEmail();
                    setShowActions(false);
                  }}
                >
                  <Mail size={14} className="mr-2" />
                  Send Email
                </button>
                <hr className="my-1" />
                {canMoveLeft && (
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveStage(application.id, allStages[currentStageIndex - 1].name);
                      setShowActions(false);
                    }}
                  >
                    ← Move to {allStages[currentStageIndex - 1]?.label}
                  </button>
                )}
                {canMoveRight && (
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveStage(application.id, allStages[currentStageIndex + 1].name);
                      setShowActions(false);
                    }}
                  >
                    → Move to {allStages[currentStageIndex + 1]?.label}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HiringPipeline;
